/* WorkTrigGridViewRenderer.js */

"use strict";

const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");
const _ = require("lodash");

class WorkTrigGridViewRenderer {
    constructor(model) {
        // this is work trigger grid model
        this._model = model;
        this._rootElem = document.createElement("worktrig-grid-view-gl");
        this._logger = this._model.logger;
        this._defaultViewActive = false;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("FeedInitialized", (this._onFeedInitialized = this._onFeedInitialized.bind(this)));
        this._model.pagingFeed.on("RefreshView", (this._onFeedRefreshView = this._onFeedRefreshView.bind(this)));
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._triggerTypeEntityGrid.on("UserSelectedRows", (this._onGridUserSelectedRows = this._onGridUserSelectedRows.bind(this)));
        this._triggerTypeEntityGrid.on("UserRowDoubleClicked", (this._onUserDblClickedRow = this._onUserDblClickedRow.bind(this)));
        this._triggerTypeEntityGrid.on("UserSorted", (this._onUserSorted = this._onUserSorted.bind(this)));
    }

    _unregisterListeners() {
        this._model.removeListener("FeedInitialized", this._onFeedInitialized);
        this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        if (this._triggerTypeEntityGrid) {
            this._unregisterGridListeners();
        }
    }

    _unregisterGridListeners() {
        this._triggerTypeEntityGrid.removeAllListeners("UserSelectedRows");
        this._triggerTypeEntityGrid.removeAllListeners("UserRowDoubleClicked");
        this._triggerTypeEntityGrid.removeListener("UserSorted", this._onUserSorted);
    }

    _onFeedInitialized() {
        this._renderAvailableActions();
    }

    _onUserSorted(eventData) {
        this._model.onUserSorted(eventData);
    }

    _onUserDblClickedRow(obj) {
        this._model.openWorkTriggerInstanceEditor(obj.selectedRow, Registrations.WTE_VIEW_DETAILS);
    }

    _renderAvailableActions() {
        let numSelected = this.selectedRows.length || 0;
        if (numSelected > 0) {
            this._model.gameEditor.enableAllActions();
        } else {
            this._model.gameEditor.disableAllActions();
            this._model.gameEditor.enableAction(Registrations.WORK_TRIGGER_EXPLORER_ACTION_CREATE);
        }

        if (numSelected > 1) {
            this._model.gameEditor.disableAction(Registrations.WORK_TRIGGER_EXPLORER_ACTION_CLONE);
        }
    }

    _showDefaultMessageDisplay() {
        return this._model.gameEditor
            .showDefaultMessageView({
                message: "No schedules available"
            })
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn({ error: err }, "WorkTrigGridViewRenderer._showDefaultMessageDisplay error");
            });
    }

    _hideDefaultMessageDisplay() {
        return this._model.gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn({ error: err }, "WorkTrigGridViewRenderer._hideDefaultMessageDisplay error");
            });
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        const filterData = this._model.pagingFeed.getFilterData();

        if (totalEntities === 0 && _.isEmpty(filterData)) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        return (this._triggerTypeEntityGrid ? this._triggerTypeEntityGrid.shutdown() : Promise.resolve()).then(
            () => {
                this._triggerTypeEntityGrid = undefined;
                this._defaultViewActive = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn(err, "WorkTrigGridViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model.whenModelIsReady().then(() => {
            this._triggerTypeEntityGrid = new EntityGrid({
                gridKey: this._model._gridKey,
                logger: this._logger,
                singleSelect: false,
                columns: this._model.columnConfig,
                pagingFeed: this._model.pagingFeed,
                showHeader: true,
                showLinesNumber: this._model._gameEditor.renderer._rootElem.querySelector("game-editor-view-actions-gl left-container"),
                columnSettingsWidgetContainer: this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                userGridData: this._model._userGridData,
                filterComponent: this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                advancedFilterComponent: this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left")
            });

            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._triggerTypeEntityGrid.renderer.rootElement);
            }

            this._renderAvailableActions();
            this._applyViewSettings(this.model.getViewSettings());
            this._registerListeners();
            this._onFeedRefreshView(); //on load, show default message as if a refresh was done

            if (
                this._model._gameEditor._openEditorFromUrl &&
                (this._model._gameEditor._openEditorFromUrl.open || !_.isUndefined(this._model._gameEditor._openEditorFromUrl.selectedIds))
            ) {
                _.get(this._model._gameEditor._openEditorFromUrl, "selectedIds", []).forEach((policyid) => {
                    this._model.openWorkTriggerInstanceEditor(policyid, Registrations.WTE_VIEW_DETAILS);
                });
            }

            if (this._model._gameEditor._filterFromUrl) {
                this._triggerTypeEntityGrid._advancedFilter._advancedFilter._renderer._applyQuery();
            }
        });
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    get selectedRows() {
        return this._triggerTypeEntityGrid.getSelectedRows();
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onGridUserSelectedRows(selectedRowIds) {
        this._model.updateExplorerState("selectedIds", selectedRowIds || []);
        this._renderAvailableActions();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _applyViewSettings(viewSettings) {
        if (viewSettings && viewSettings.triggerTypeId) {
            return this._triggerTypeEntityGrid.pagingFeed.ensureEntityOnPage(viewSettings.triggerTypeId).then(() => {
                this._triggerTypeEntityGrid.setSelectedRows([viewSettings.triggerTypeId]);
                this._model.openWorkTriggerInstanceEditor(viewSettings.triggerTypeId, viewSettings.view, viewSettings);
            });
        }
    }
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = WorkTrigGridViewRenderer;
